import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useU2000AxiosAuthenticated from '../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../u2000Core/axios/U2000HandleApiError';
import { u2000TimesheetWeekIsoDateList } from '../u2000Core/tools/U2000TimesheetHonorariumDayTotals';
import { useToast } from '../u2000Core/context/toast/U2000ToastContext';
import { NS } from './U3147';
import { doDay, luHandleApiError, type U3147DayResult } from './U3147Service';

export type U3147LoadWeekDaysOptions = {
  /** Si true, les erreurs API sont ignorées (ex. 403 OTHEREMPLOYEE depuis U3130). */
  silentErrors?: boolean;
};

function u3147IndexDayResults(
  results: PromiseSettledResult<{ requestedDate: string; result: U3147DayResult }>[],
): Record<string, U3147DayResult> {
  const next: Record<string, U3147DayResult> = {};
  for (const settled of results) {
    if (settled.status !== 'fulfilled') continue;
    const { requestedDate, result } = settled.value;
    if (!result || typeof result !== 'object') continue;
    const key = (result.date?.slice(0, 10) || requestedDate).slice(0, 10);
    next[key] = result;
  }
  return next;
}

export function useU3147WeekDaySessionsHandlers() {
  const axios = useU2000AxiosAuthenticated();
  const { t } = useTranslation();
  const toast = useToast();

  const loadWeekDays = useCallback(
    async (
      periodDate: string,
      employeeId?: string,
      options?: U3147LoadWeekDaysOptions,
    ): Promise<Record<string, U3147DayResult>> => {
      const weekDates = u2000TimesheetWeekIsoDateList(periodDate);
      if (!weekDates?.length) return {};

      const employeePayload = employeeId?.trim() ? { employeeId: employeeId.trim() } : {};
      const results = await Promise.allSettled(
        weekDates.map((date) =>
          doDay(axios, { date: date.slice(0, 10), ...employeePayload }).then((response) => {
            const raw = (response.data?.result ?? response.data) as U3147DayResult;
            return { requestedDate: date.slice(0, 10), result: raw };
          }),
        ),
      );

      if (!options?.silentErrors) {
        for (const settled of results) {
          if (settled.status !== 'fulfilled') {
            const errorResult = U2000HandleApiError(settled.reason as Error, t, NS, luHandleApiError);
            if (errorResult.genericErrors?.length) {
              errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
            }
          }
        }
      }

      return u3147IndexDayResults(results);
    },
    [axios, t, toast],
  );

  return { loadWeekDays };
}

export function useU3147WeekDaySessions(
  periodDate: string | null | undefined,
  employeeId: string | undefined,
  enabled: boolean,
): { byDate: Record<string, U3147DayResult>; loading: boolean } {
  const { loadWeekDays } = useU3147WeekDaySessionsHandlers();
  const [byDate, setByDate] = useState<Record<string, U3147DayResult>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !periodDate?.trim()) {
      setByDate({});
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setByDate({});

    void loadWeekDays(periodDate, employeeId, { silentErrors: true }).then((next) => {
      if (cancelled) return;
      setByDate(next);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [loadWeekDays, periodDate, employeeId, enabled]);

  return { byDate, loading };
}
